import { queryOptions } from "@tanstack/react-query";
import { isAxiosError } from "axios";
import { Element } from "@/components/elements/UIElement";
import { normalizeRequestError } from "@/lib/errors";
import { request } from "@/lib/request";
import { RequestConfigType } from "@/store/slices/requestsSlice";
import { normalizeElementsResponse } from "./elementsTransport";

export type ElementsQueryData = {
  elements: Element[];
  requests: RequestConfigType[];
  elementsVersion: number;
};

export type ElementsPreconditionFailedErrorData = {
  code: "ELEMENTS_PRECONDITION_FAILED";
  message: string;
  serverVersion: number;
  serverEtag: string;
};

export const elementsQueryKey = (profileId?: string | null) =>
  ["elements", profileId || null] as const;

/**
 * Shared key+fn for /profile/:id/elements — consumed by useGetElements and
 * prefetched at boot (main.tsx) for the stored profile id, so the two dedupe
 * into one request. Access-token gating stays in the hook via `enabled`.
 */
export const elementsQueryOptions = (profileId?: string | null) =>
  queryOptions({
    queryKey: elementsQueryKey(profileId),
    queryFn: async ({ signal }): Promise<ElementsQueryData> => {
      if (!profileId) {
        return { elements: [], requests: [], elementsVersion: 0 };
      }

      const res = await request
        .get(`/profile/${profileId}/elements`, { signal })
        .catch(
          normalizeRequestError("Something went wrong, please try again."),
        );

      return normalizeElementsResponse(res.data?.data);
    },
    staleTime: 30_000,
  });

export const toElementsEtag = (elementsVersion: number): string =>
  `"elements-v${Math.max(0, Math.trunc(elementsVersion || 0))}"`;

export const isElementsPreconditionFailedError = (
  error: unknown,
): error is {
  response: {
    data: ElementsPreconditionFailedErrorData;
    status: number;
  };
} =>
  isAxiosError(error) &&
  error.response?.status === 412 &&
  error.response?.data?.code === "ELEMENTS_PRECONDITION_FAILED" &&
  typeof error.response?.data?.serverVersion === "number" &&
  typeof error.response?.data?.serverEtag === "string";
